import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import ProgressBar from '@/components/ProgressBar';
import SponsorWidget from '@/components/SponsorWidget';
import MediaGrid from '@/components/MediaGrid';

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

interface ProspectGoal {
  id: string;
  title: string;
  description?: string;
  raised_cents: number;
  target_cents: number;
}

interface ProspectUpdate {
  id: string;
  [key: string]: unknown;
}

interface Prospect {
  id: string;
  name: string;
  headline?: string;
  bio?: string;
  avatar_url?: string;
  cover_url?: string;
  sport?: string;
  position?: string;
  city?: string;
  state?: string;
  school?: string;
  goals?: ProspectGoal[];
  updates?: ProspectUpdate[];
}

function GoalCard({ goal }: { goal: ProspectGoal }) {
  const percent = goal.target_cents > 0 ? Math.min(100, Math.round((goal.raised_cents / goal.target_cents) * 100)) : 0;
  return (
    <div className="rounded-2xl bg-white/80 p-4 shadow-soft">
      <h4 className="font-semibold text-cocoa">{goal.title}</h4>
      {goal.description ? <p className="mt-1 text-sm text-cocoa/70">{goal.description}</p> : null}
      <div className="mt-3 text-sm text-cocoa/70">
        {percent}% funded — {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(goal.raised_cents / 100)}
        {' '}
        raised of {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(goal.target_cents / 100)} goal
      </div>
      <div className="mt-3">
        <ProgressBar value={goal.raised_cents} max={goal.target_cents} />
      </div>
    </div>
  );
}

export default function ProspectDetail() {
  const { id } = useParams();
  const [prospect, setProspect] = useState<Prospect | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${CORE_API}/api/v1/prospects/detail?id=${encodeURIComponent(id ?? '')}`);
        if (!res.ok) throw new Error('Failed to load prospect');
        const raw = await res.json();
        if (!active) return;
        setProspect(raw.data ?? raw);
        setError(null);
      } catch (err: unknown) {
        if (!active) return;
        setError(err instanceof Error ? err.message : 'Failed to load prospect');
      } finally {
        if (active) setLoading(false);
      }
    };
    if (id) load();
    return () => {
      active = false;
    };
  }, [id]);

  const primaryGoal = useMemo(() => prospect?.goals?.[0] || null, [prospect]);

  if (loading) {
    return <div className="rounded-2xl border border-sand/60 bg-white/70 p-6 text-center text-cocoa/70">Loading athlete…</div>;
  }

  if (error || !prospect) {
    return <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center text-rose-600">{error || 'Prospect not found'}</div>;
  }

  return (
    <div className="space-y-12">
      <section className="overflow-hidden rounded-3xl bg-white shadow-soft">
        <div className="relative h-56 w-full bg-sand/50">
          {prospect.cover_url ? (
            <img src={prospect.cover_url} alt="Prospect hero" className="h-full w-full object-cover" />
          ) : null}
          <div className="absolute inset-x-0 bottom-0 flex items-end gap-4 bg-gradient-to-t from-black/60 via-black/10 to-transparent p-6">
            {prospect.avatar_url ? (
              <img src={prospect.avatar_url} alt={prospect.name} className="h-28 w-28 rounded-full border-4 border-white object-cover shadow-lg" />
            ) : null}
            <div className="text-white">
              <h1 className="text-3xl font-semibold">{prospect.name}</h1>
              {prospect.headline ? <p className="text-sm text-white/80">{prospect.headline}</p> : null}
              <p className="mt-2 text-xs uppercase tracking-wide text-white/70">
                {prospect.sport}
                {prospect.position ? ` • ${prospect.position}` : ''}
                {prospect.city || prospect.state ? ` • ${[prospect.city, prospect.state].filter(Boolean).join(', ')}` : ''}
              </p>
            </div>
          </div>
        </div>
        <div className="grid gap-8 p-6 lg:grid-cols-[2fr,1fr]">
          <div className="space-y-6">
            {prospect.bio ? <p className="text-cocoa/80">{prospect.bio}</p> : null}
            {prospect.goals?.length ? (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-cocoa">Funding goals</h2>
                <div className="space-y-4">
                  {prospect.goals.map((goal) => (
                    <GoalCard key={goal.id} goal={goal} />
                  ))}
                </div>
              </div>
            ) : null}
            {prospect.updates?.length ? (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-cocoa">Latest updates</h2>
                <MediaGrid items={prospect.updates} />
              </div>
            ) : null}
          </div>
          <div className="space-y-6">
            <SponsorWidget prospectId={prospect.id} goalId={primaryGoal?.id ?? ''} />
            {prospect.school ? (
              <div className="rounded-2xl bg-sand/50 p-4 text-sm text-cocoa/70">
                <h3 className="text-base font-semibold text-cocoa">School & Club</h3>
                <p className="mt-1 text-cocoa/80">{prospect.school}</p>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}
